import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  api,
  getAccessToken,
  getOrganizationId,
  getSelectedContext,
  getStaffToken,
} from "@/lib/api";
import { appConfig } from "@/lib/config";
import { retryUnlessClientError } from "@/lib/queryRetry";
import type {
  SubscriptionResponse,
  VerifyPaymentRequest,
  WalletResponse,
  WalletRechargeRequest,
  WalletRechargeResponse,
  VerifyWalletPaymentResponse,
  TransactionHistoryResponse,
  TransactionHistoryParams,
  SupportAddonResponse,
  SupportAddonPurchaseResponse,
  VerifySupportAddonPaymentResponse,
  UsageBillingResponse,
} from "@/types/billing";

// ==========================================
// Helpers
// ==========================================

const requireOrgId = (): string => {
  const orgId = getOrganizationId();
  if (!orgId) throw new Error("Organization not found. Please re-select your outlet.");
  return orgId;
};

const getOutletId = (): string | null => {
  const context = getSelectedContext();
  return context?.outlet?.id ?? null;
};

const requireOutletId = (): string => {
  const id = getOutletId();
  if (!id) throw new Error("No outlet selected. Please select an outlet first.");
  return id;
};

/** Base path for outlet-scoped billing endpoints. */
const billingBase = (orgId: string, outletId: string) =>
  `/api/v1/billing/${orgId}/outlets/${outletId}`;

// ==========================================
// Subscription Queries
// ==========================================

export const isSubscriptionExpired = (
  _subscription: SubscriptionResponse | null | undefined
): boolean => {
  return false;
};

/**
 * Thrown by fetchSubscription for any response that isn't a definitive
 * answer about subscription state. Carries the HTTP status so the calling
 * query can decide whether to retry. Status 0 means a network / client-side
 * failure (fetch threw).
 */
export class SubscriptionFetchError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = "SubscriptionFetchError";
  }
}

// Raw fetch (not api.ts) so that a failing subscription check — e.g. 401 from
// the billing endpoint — can't trigger api.ts's session-clear + hard-redirect
// path and kick the user back to /select-context mid-flow.
//
// IMPORTANT: transport failures (401/403/5xx/network) are NOT laundered into
// "no subscription" any more. Previously we normalized all non-2xx to `null`,
// which made any token blip or transient network error present as "subscription
// expired" to the guard. Now:
//   - 200 → parsed body; a null (or empty) body means "no subscription"
//   - 404 → null (no plan on file; older API, or an unknown outlet)
//   - everything else → throw SubscriptionFetchError so React Query can retry
export const fetchSubscription = async (
  orgId: string,
  outletId: string
): Promise<SubscriptionResponse | null> => {
  const accessToken = getAccessToken();
  const staffToken = getStaffToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    "X-Org-ID": orgId,
  };
  if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;
  if (staffToken) headers["X-Staff-Token"] = staffToken;

  let res: Response;
  try {
    res = await fetch(
      `${appConfig.apiBaseUrl}${billingBase(orgId, outletId)}/subscription`,
      { headers }
    );
  } catch (err) {
    throw new SubscriptionFetchError(
      0,
      err instanceof Error ? err.message : "network error"
    );
  }

  if (res.status === 404) return null;
  if (!res.ok) {
    throw new SubscriptionFetchError(
      res.status,
      `subscription fetch failed with ${res.status}`
    );
  }
  const body = (await res.text()).trim();
  if (!body) return null;
  return (JSON.parse(body) as SubscriptionResponse | null) ?? null;
};

export const useSubscription = () => {
  const orgId = getOrganizationId();
  const outletId = getOutletId();
  return useQuery({
    queryKey: ["billing-subscription", orgId, outletId],
    queryFn: () => fetchSubscription(orgId!, outletId!),
    enabled: !!orgId && !!outletId,
    retry: retryUnlessClientError,
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
  });
};

// ==========================================
// Wallet Queries
// ==========================================

export const useWalletBalance = () => {
  const orgId = getOrganizationId();
  const outletId = getOutletId();
  return useQuery({
    queryKey: ["billing-wallet", orgId, outletId],
    queryFn: () =>
      api.get<WalletResponse>(`${billingBase(orgId!, outletId!)}/wallet`),
    enabled: !!orgId && !!outletId,
  });
};

// ==========================================
// Wallet Mutations
// ==========================================

export const useRechargeWallet = () => {
  return useMutation({
    mutationFn: (data: WalletRechargeRequest) => {
      const orgId = requireOrgId();
      const outletId = requireOutletId();
      return api.post<WalletRechargeResponse>(
        `${billingBase(orgId, outletId)}/wallet/recharge`,
        data
      );
    },
  });
};

export const useVerifyWalletPayment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: VerifyPaymentRequest) => {
      const orgId = requireOrgId();
      const outletId = requireOutletId();
      return api.post<VerifyWalletPaymentResponse>(
        `${billingBase(orgId, outletId)}/wallet/verify-payment`,
        data
      );
    },
    onSuccess: () => {
      const orgId = getOrganizationId();
      const outletId = getOutletId();
      queryClient.invalidateQueries({
        queryKey: ["billing-wallet", orgId, outletId],
      });
      queryClient.invalidateQueries({
        queryKey: ["billing-transactions", orgId, outletId],
      });
      queryClient.invalidateQueries({
        queryKey: ["billing-usage", orgId, outletId],
      });
    },
  });
};

// ==========================================
// Transaction Queries
// ==========================================

export const useTransactionHistory = (
  params: TransactionHistoryParams = {}
) => {
  const orgId = getOrganizationId();
  const outletId = getOutletId();
  return useQuery({
    queryKey: ["billing-transactions", orgId, outletId, params],
    queryFn: () => {
      const searchParams = new URLSearchParams();
      if (params.page) searchParams.append("page", String(params.page));
      if (params.page_size)
        searchParams.append("page_size", String(params.page_size));
      if (params.type) searchParams.append("type", params.type);
      if (params.status) searchParams.append("status", params.status);
      if (params.from_date) searchParams.append("from_date", params.from_date);
      if (params.to_date) searchParams.append("to_date", params.to_date);
      const qs = searchParams.toString();
      return api.get<TransactionHistoryResponse>(
        `${billingBase(orgId!, outletId!)}/transactions${qs ? `?${qs}` : ""}`
      );
    },
    enabled: !!orgId && !!outletId,
  });
};

export const useUsageBilling = () => {
  const orgId = getOrganizationId();
  const outletId = getOutletId();
  const isDemo = getSelectedContext()?.outlet?.is_demo ?? false;
  return useQuery({
    queryKey: ["billing-usage", orgId, outletId],
    queryFn: () =>
      api.get<UsageBillingResponse>(`${billingBase(orgId!, outletId!)}/usage`),
    enabled: !!orgId && !!outletId && !isDemo,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  });
};

export const useSupportAddon = () => {
  const orgId = getOrganizationId();
  return useQuery({
    queryKey: ["billing-support-addon", orgId],
    queryFn: () => api.get<SupportAddonResponse>(`/api/v1/billing/${orgId}/support-addon`),
    enabled: !!orgId,
  });
};

export const usePurchaseSupportAddon = () => {
  return useMutation({
    mutationFn: (data: { idempotency_key?: string; outlet_id?: string }) => {
      const orgId = requireOrgId();
      const outletId = data.outlet_id ?? getSelectedContext()?.outlet?.id;
      return api.post<SupportAddonPurchaseResponse>(
        `/api/v1/billing/${orgId}/support-addon/purchase`,
        {
          idempotency_key: data.idempotency_key,
          outlet_id: outletId ?? undefined,
        }
      );
    },
  });
};

export const useVerifySupportAddonPayment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: VerifyPaymentRequest) => {
      const orgId = requireOrgId();
      return api.post<VerifySupportAddonPaymentResponse>(
        `/api/v1/billing/${orgId}/support-addon/verify-payment`,
        data
      );
    },
    onSuccess: () => {
      const orgId = getOrganizationId();
      queryClient.invalidateQueries({ queryKey: ["billing-support-addon", orgId] });
      queryClient.invalidateQueries({ queryKey: ["billing-transactions"] });
    },
  });
};
